/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */

/*
 * I negozi da cui si compra fuori magazzino.
 *
 * Non e' un'anagrafica per completezza: serve a rispondere a "quanto abbiamo
 * speso da questo fornitore quest'anno", e quella domanda ha senso solo se lo
 * stesso negozio e' una riga sola. Per questo la chiave e' la partita IVA e non
 * il nome, e per questo la form insiste su quel campo.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, X, Edit2, Archive, RotateCcw, Truck, AlertTriangle } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { fornitoriAPI } from '../api/fornitori';
import { useViewport } from '../hooks/useViewport';

const ACCENT = '#8B5CF6';

const eur = (v) => `€ ${Number(v || 0).toFixed(2)}`;

export default function Fornitori() {
  const { isCompact } = useViewport();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [mostraArchiviati, setMostraArchiviati] = useState(false);
  const [inModifica, setInModifica] = useState(null); // fornitore, oppure 'nuovo'

  const { data: fornitori = [], isLoading } = useQuery({
    queryKey: ['fornitori', search, mostraArchiviati],
    queryFn: () => fornitoriAPI.list({
      q: search || undefined,
      attivo: mostraArchiviati ? undefined : 'true',
    }),
  });

  const aggiorna = () => queryClient.invalidateQueries({ queryKey: ['fornitori'] });
  const archivia = useMutation({
    mutationFn: ({ id, attivo }) => fornitoriAPI.archivia(id, attivo),
    onSuccess: aggiorna,
  });

  const senzaPiva = fornitori.filter((f) => !f.partita_iva && f.attivo).length;

  return (
    <AdminLayout title="Fornitori">
      <div style={styles.toolbar}>
        <div style={styles.searchBox}>
          <Search size={16} color="#9CA3AF" />
          <input
            style={styles.searchInput}
            placeholder="Cerca per nome o partita IVA..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && <button style={styles.clearBtn} onClick={() => setSearch('')}><X size={14} /></button>}
        </div>
        <button
          style={{ ...styles.chip, ...(mostraArchiviati ? styles.chipAttivo : {}) }}
          onClick={() => setMostraArchiviati((v) => !v)}
        >
          <Archive size={14} /> Archiviati
        </button>
        <button style={styles.primaryBtn} onClick={() => setInModifica('nuovo')}>
          <Plus size={16} /> Nuovo fornitore
        </button>
      </div>

      {senzaPiva > 0 && (
        <div style={styles.avviso}>
          <AlertTriangle size={16} />
          <div>
            {senzaPiva} fornitor{senzaPiva > 1 ? 'i' : 'e'} senza partita IVA. Funzionano lo
            stesso, ma sono gli unici che possono diventare doppioni: e' il nome a
            distinguerli, e lo stesso negozio scritto in due modi diventa due righe.
          </div>
        </div>
      )}

      <div style={styles.tableWrapper}>
        {isLoading ? (
          <div style={styles.empty}>Caricamento...</div>
        ) : fornitori.length === 0 ? (
          <div style={styles.empty}>
            <Truck size={40} color="#9CA3AF" />
            <p>{search ? 'Nessun fornitore trovato.' : 'Nessun fornitore registrato.'}</p>
            {!search && (
              <p style={styles.emptyHint}>
                Si aggiungono da qui oppure direttamente dal tecnico, quando registra
                un materiale comprato fuori magazzino.
              </p>
            )}
          </div>
        ) : isCompact ? (
          <div style={styles.cardList}>
            {fornitori.map((f) => (
              <div key={f.id} style={{ ...styles.card, ...(f.attivo ? {} : styles.archiviato) }}>
                <div style={{ fontWeight: 700 }}>{f.denominazione}</div>
                <div style={styles.cardMeta}>
                  {f.partita_iva ? <code style={styles.piva}>{f.partita_iva}</code> : <span style={styles.senzaPiva}>senza P.IVA</span>}
                  {!f.attivo && <span style={styles.archiviatoTag}>archiviato</span>}
                </div>
                <div style={styles.cardMeta}>{f.acquisti} acquisti · {eur(f.speso)}</div>
                <div style={styles.azioni}>
                  <button style={styles.actionBtn} onClick={() => setInModifica(f)}><Edit2 size={15} /> Modifica</button>
                  <button style={styles.actionBtn} onClick={() => archivia.mutate({ id: f.id, attivo: !f.attivo })}>
                    {f.attivo ? <><Archive size={15} /> Archivia</> : <><RotateCcw size={15} /> Ripristina</>}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr style={styles.thead}>
                <th style={styles.th}>Denominazione</th>
                <th style={styles.th}>Partita IVA</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Acquisti</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Speso</th>
                <th style={styles.th}>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {fornitori.map((f) => (
                <tr key={f.id} style={{ ...styles.tr, ...(f.attivo ? {} : styles.archiviato) }}>
                  <td style={styles.td}>
                    <strong>{f.denominazione}</strong>
                    {!f.attivo && <span style={styles.archiviatoTag}>archiviato</span>}
                    {f.note && <div style={styles.note}>{f.note}</div>}
                  </td>
                  <td style={styles.td}>
                    {f.partita_iva
                      ? <code style={styles.piva}>{f.partita_iva}</code>
                      : <span style={styles.senzaPiva}>—</span>}
                    {f.codice_fiscale && <div style={styles.note}>CF {f.codice_fiscale}</div>}
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{f.acquisti}</td>
                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700 }}>{eur(f.speso)}</td>
                  <td style={styles.td}>
                    <div style={styles.azioni}>
                      <button style={styles.actionBtn} onClick={() => setInModifica(f)}>
                        <Edit2 size={15} /> Modifica
                      </button>
                      <button
                        style={styles.actionBtn}
                        onClick={() => archivia.mutate({ id: f.id, attivo: !f.attivo })}
                        title={f.attivo
                          ? 'Lo toglie dall\'elenco. Gli acquisti gia\' fatti restano.'
                          : 'Lo rimette in elenco.'}
                      >
                        {f.attivo ? <Archive size={15} /> : <RotateCcw size={15} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {inModifica && (
        <FornitoreFormModal
          fornitore={inModifica === 'nuovo' ? null : inModifica}
          onClose={() => setInModifica(null)}
          onSaved={() => { setInModifica(null); aggiorna(); }}
        />
      )}
    </AdminLayout>
  );
}

function FornitoreFormModal({ fornitore, onClose, onSaved }) {
  const nuovo = !fornitore;
  const [form, setForm] = useState({
    denominazione: fornitore?.denominazione || '',
    partita_iva: fornitore?.partita_iva || '',
    codice_fiscale: fornitore?.codice_fiscale || '',
    note: fornitore?.note || '',
  });
  const [errore, setErrore] = useState('');
  // Quando il server dice che la P.IVA esiste gia', qui finisce il fornitore che
  // ha trovato: cosi' invece di un errore secco si puo' mostrare chi e'.
  const [giaEsistente, setGiaEsistente] = useState(null);

  const salva = useMutation({
    mutationFn: (payload) => (nuovo
      ? fornitoriAPI.crea(payload)
      : fornitoriAPI.modifica(fornitore.id, payload)),
    onSuccess: onSaved,
    onError: (e) => {
      setErrore(e.response?.data?.error || 'Errore nel salvataggio');
      setGiaEsistente(e.response?.data?.fornitore || null);
    },
  });

  const onSubmit = (e) => {
    e.preventDefault();
    setErrore('');
    setGiaEsistente(null);
    if (!form.denominazione.trim()) return setErrore('La denominazione e\' obbligatoria');
    salva.mutate(form);
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <form style={styles.modal} onClick={(e) => e.stopPropagation()} onSubmit={onSubmit}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>{nuovo ? 'Nuovo fornitore' : 'Modifica fornitore'}</h2>
          <button type="button" style={styles.iconBtn} onClick={onClose}><X size={18} /></button>
        </div>

        <div style={styles.modalBody}>
          <label style={styles.label}>Denominazione *</label>
          <input
            style={styles.input}
            value={form.denominazione}
            onChange={(e) => setForm({ ...form, denominazione: e.target.value })}
            placeholder="Come e' scritto sullo scontrino"
            autoFocus
          />

          <label style={styles.label}>Partita IVA</label>
          <input
            style={styles.input}
            value={form.partita_iva}
            onChange={(e) => setForm({ ...form, partita_iva: e.target.value })}
            placeholder="11 cifre, con o senza IT"
          />
          <div style={styles.hint}>
            E' il campo che tiene unito lo stesso negozio nel tempo. Spazi e prefisso IT
            si possono scrivere: vengono tolti da soli. Le cifre vengono controllate.
          </div>

          <label style={styles.label}>Codice fiscale</label>
          <input
            style={styles.input}
            value={form.codice_fiscale}
            onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value })}
            placeholder="solo se diverso dalla partita IVA"
          />

          <label style={styles.label}>Note</label>
          <textarea
            style={{ ...styles.input, minHeight: '70px', resize: 'vertical' }}
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            placeholder="Es. reparto idraulica, referente, condizioni"
          />

          {errore && (
            <div style={styles.errore}>
              {errore}
              {giaEsistente && (
                <div style={styles.hintErrore}>
                  Usa quello che c'e' gia' invece di crearne un altro: e' lo stesso
                  negozio, e due righe renderebbero inutile il conteggio degli acquisti.
                </div>
              )}
            </div>
          )}
        </div>

        <div style={styles.modalActions}>
          <button type="button" style={styles.secondaryBtn} onClick={onClose}>Annulla</button>
          <button type="submit" style={styles.primaryBtn} disabled={salva.isPending}>
            {salva.isPending ? 'Salvataggio...' : 'Salva'}
          </button>
        </div>
      </form>
    </div>
  );
}

const styles = {
  toolbar: { display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem' },
  searchBox: { display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '10px', padding: '0.5rem 0.7rem', flex: '1 1 240px', minWidth: 0 },
  searchInput: { border: 'none', outline: 'none', fontSize: '0.9rem', flex: 1, minWidth: 0 },
  clearBtn: { border: 'none', background: 'none', cursor: 'pointer', color: '#9CA3AF', display: 'flex', padding: 0 },
  chip: { display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.5rem 0.75rem', border: '1px solid #E5E7EB', borderRadius: '10px', backgroundColor: 'white', color: '#374151', fontSize: '0.85rem', cursor: 'pointer', minHeight: '40px' },
  chipAttivo: { backgroundColor: ACCENT + '15', borderColor: ACCENT, color: ACCENT, fontWeight: 600 },
  primaryBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', padding: '0.55rem 1rem', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '10px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', minHeight: '40px' },
  secondaryBtn: { padding: '0.55rem 1rem', backgroundColor: '#F3F4F6', color: '#374151', border: 'none', borderRadius: '10px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', minHeight: '40px' },

  avviso: { display: 'flex', gap: '0.6rem', alignItems: 'flex-start', padding: '0.75rem 0.9rem', backgroundColor: '#FEF3C7', border: '1px solid #FDE68A', color: '#92400E', borderRadius: '10px', fontSize: '0.86rem', marginBottom: '1rem', lineHeight: 1.5 },

  tableWrapper: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { backgroundColor: '#F9FAFB' },
  th: { padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap' },
  tr: { borderTop: '1px solid #F3F4F6' },
  archiviato: { opacity: 0.55 },
  td: { padding: '0.75rem 1rem', fontSize: '0.88rem', color: '#111827', verticalAlign: 'top' },
  piva: { fontFamily: 'monospace', backgroundColor: '#F3F4F6', padding: '0.1rem 0.35rem', borderRadius: '4px', fontSize: '0.82rem' },
  senzaPiva: { color: '#9CA3AF', fontStyle: 'italic', fontSize: '0.82rem' },
  archiviatoTag: { color: '#9CA3AF', fontStyle: 'italic', fontSize: '0.78rem', marginLeft: '0.4rem' },
  note: { fontSize: '0.78rem', color: '#6B7280', marginTop: '0.2rem' },
  azioni: { display: 'flex', gap: '0.35rem', flexWrap: 'wrap' },
  actionBtn: { display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.4rem 0.55rem', backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '0.8rem', color: '#374151', cursor: 'pointer', minHeight: '36px' },
  empty: { padding: '3rem 1.5rem', textAlign: 'center', color: '#6B7280', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' },
  emptyHint: { fontSize: '0.85rem', color: '#9CA3AF', maxWidth: '440px' },

  cardList: { display: 'flex', flexDirection: 'column', gap: '0.75rem', padding: '0.75rem' },
  card: { border: '1px solid #E5E7EB', borderRadius: '10px', padding: '0.85rem', display: 'flex', flexDirection: 'column', gap: '0.3rem' },
  cardMeta: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap', fontSize: '0.8rem', color: '#6B7280', alignItems: 'center' },

  modalOverlay: { position: 'fixed', top: 0, bottom: 0, left: 0, right: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem' },
  modal: { backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '480px', maxHeight: '90vh', overflow: 'auto', display: 'flex', flexDirection: 'column' },
  modalHeader: { padding: '1.1rem 1.25rem', borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  modalTitle: { margin: 0, fontSize: '1.05rem', fontWeight: 700 },
  modalBody: { padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  modalActions: { padding: '1rem 1.25rem', borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'flex-end', gap: '0.6rem' },
  iconBtn: { border: 'none', background: 'none', cursor: 'pointer', color: '#6B7280', display: 'flex', padding: '0.25rem' },
  label: { fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginTop: '0.6rem' },
  input: { width: '100%', padding: '0.6rem 0.7rem', border: '1px solid #E5E7EB', borderRadius: '10px', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' },
  hint: { fontSize: '0.78rem', color: '#6B7280', marginTop: '0.35rem' },
  errore: { marginTop: '0.85rem', padding: '0.7rem 0.8rem', backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: '8px', fontSize: '0.86rem', lineHeight: 1.5 },
  hintErrore: { marginTop: '0.4rem', fontSize: '0.8rem', fontWeight: 400 },
};
